import { useState, useEffect } from "react";
import { Field } from "formik";
import { FaExclamationCircle } from "react-icons/fa";
import ModalButton from "../Ui/Buttons/ModalButtons";
import style from "../EmailCheck/emailCheck.module.css";
import ModalComponent from "../Ui/ModalComponent/ModalWindow";
import { sentEmail, verifyEmailApi, registerUser } from "../../common/api/authApi";
import { log } from "node:console";
import { verify } from "node:crypto";
import { useNavigate } from "react-router-dom";

interface EmailCheckModalProps {
    isOpen: boolean;
    email: string;
    onClose: () => void;
    formik: any;
    action: string;
    setErrorModal: (error: any) => void;
}

const EmailCheckModal: React.FC<EmailCheckModalProps> = ({
    isOpen,
    email,
    onClose,
    formik,
    action,
    setErrorModal
}) => {
    const [timeLeft, setTimeLeft] = useState(300); // 300 секунд (5 минут)
    const [failedAttempts, setFailedAttempts] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        if (!isOpen) {
            setFailedAttempts(0); // Reset attempts when modal closes
            // Сбросить поле secretCode
            formik.setFieldValue("secretCode", "");
            return;
        }
        console.log(formik.values.email);

        setTimeLeft(300); // Сброс таймера при повторном открытии

        const interval = setInterval(() => {
            setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        if (action === "registration") {
            sentEmail(formik.values.email)
                .then(response => {
                    console.log('Email check response:', response);
                    if (response.message?.includes('минуты')) {
                        onClose();
                        setErrorModal({
                            hasError: true,
                            errorMessage: response.message
                        });
                    }
                    else if (response.status === 0 && response.message === "this email exists in our system") {
                        onClose();
                        setErrorModal({
                            hasError: true,
                            errorMessage: "Этот email уже зарегистрирован в системе"
                        });
                    }
                })
                .catch(error => {
                    console.error('Error sending email:', error);
                });
        }
        return () => clearInterval(interval);
    }, [isOpen]);

    const verifyEmail = async (formik: any) => {
        try {
            const response = await verifyEmailApi(formik.values.email, formik.values.secretCode);
            
            // Check if verification was successful
            if (response.status === 1) {
                // Prepare registration data
                const registrationData = {
                    login: formik.values.email,
                    password: formik.values.password,
                    confirm_password: formik.values.password,
                    first_name: formik.values.firstName,
                    last_name: formik.values.lastName,
                    second_name: formik.values.secondName || "",
                    mail_code: formik.values.secretCode
                };

                // Register the user
                const registrationResponse = await registerUser(registrationData);
                
                if (registrationResponse.status === 1) {
                    // Registration successful, navigate to main page
                    navigate("/main");
                } else {
                    formik.setFieldError("secretCode", registrationResponse.message || "Ошибка при регистрации");
                    handleFailedAttempt();
                }
            } else {
                formik.setFieldError("secretCode", response.message || "Ошибка верификации");
                handleFailedAttempt();
            }
        } catch (error: any) {
            if (error.response?.data?.status === 0 && error.response?.data?.message) {
                formik.setFieldError("secretCode", error.response.data.message);
                handleFailedAttempt();
            } else {
                formik.setFieldError("secretCode", "Произошла ошибка. Попробуйте снова.");
                handleFailedAttempt();
            }
        }
    };

    const handleFailedAttempt = () => {
        const newAttempts = failedAttempts + 1;
        setFailedAttempts(newAttempts);
        
        if (newAttempts >= 3) {
            onClose();
            setErrorModal({
                hasError: true,
                errorMessage: "Превышено количество попыток ввода кода. Пожалуйста, попробуйте зарегистрироваться снова."
            });
        }
    };

    const formatTime = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
    };

    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={onClose}
            headerContent={
                <div className="flex justify-between items-center w-full">
                    <h1 className="flex justify-center flex-1">Уведомление</h1>
                    <button 
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
                    >
                        <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            className="h-6 w-6" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                        >
                            <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M6 18L18 6M6 6l12 12" 
                            />
                        </svg>
                    </button>
                </div>
            }
            content={
                <div className="flex flex-col gap-2">
                    <p className="flex justify-center max-w-[300px] text-center">
                        Введите код подтверждения из Email сообщения, отправленного на {email}
                    </p>
                    <div className="flex justify-center">
                        <div className={style.inputField2}>
                            <div className={style.labelAndButtonWrapper}>
                                <label className={style.inputLabel2}>
                                    Код подтверждения*
                                </label>
                                {formik.touched.secretCode && formik.errors.secretCode && (
                                    <div className={style.tooltipWrapper}>
                                        <FaExclamationCircle className={style.errorIcon} />
                                        <div className={style.tooltipText}>
                                            {typeof formik.errors.secretCode === "string"
                                                ? formik.errors.secretCode
                                                : "Ошибка"}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <Field 
                                name="secretCode" 
                                placeholder="Введите код" 
                                className={`input ${formik.touched.secretCode && formik.errors.secretCode ? 'border-red-500' : ''}`}
                                disabled={timeLeft === 0}
                            />
                        </div>
                    </div>
                    <div className="flex justify-center text-gray-500 text-sm">
                        Повторный запрос кода через {formatTime(timeLeft)}
                    </div>
                    <div className="flex justify-center text-gray-500 text-sm">
                        Осталось попыток: {3 - failedAttempts}
                    </div>
                </div>
            }
            customContent={
                <div className="w-full flex justify-center">
                    <ModalButton
                        type="button"
                        color="mainColor"
                        onClick={() => verifyEmail(formik)}
                        disabled={!formik.values.secretCode || formik.values.secretCode.trim() === "" || timeLeft === 0}
                    >
                        Продолжить
                    </ModalButton>
                </div>
            }
        />
    );
};

export default EmailCheckModal;
